import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api } from '../services/api';
import { textsPath } from '../utils/textsLink';
import { businessToday } from '../utils/time';
import {
  Plus, X, Edit2, DollarSign, Hash, Eye, EyeOff,
  Phone, UserCheck, UserX, MessageSquare, Calendar, Clock, AlertTriangle, Trash2,
  Upload, FileText, CheckCircle, Search, Wallet, Sparkles, KeyRound,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useAuth } from '../services/AuthContext';
import { useReadOnly } from '../hooks/useReadOnly';

const DAY_LABELS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
// The apps a person can open (owner, Sep 18 2026: "make it into the profile").
// The login's top hat is its role; the header switch moves between the rest.
// Owner, later the same day: "combine manager and inspector" — the Manager
// hat runs the yard check AND inspects the week's lawns; Inspector is not
// offered any more. Someone still on the old hat alone shows it until they
// are given Manager (which takes it off — the server treats it as covered).
const HATS: { key: string; label: string; hint: string }[] = [
  { key: 'admin',     label: 'Admin',     hint: 'The whole admin app, approvals included.' },
  { key: 'manager',   label: 'Manager',   hint: "Runs the day: routes overview, yard walk + shop lists, client requests, lawn checks. No admin or money pages." },
  { key: 'bidder',    label: 'Bidder',    hint: 'Quote visits and written bids.' },
  // Owner, Oct 6 2026: the shop weekly checklist is "a part of the mechanic employee tag" — ticking this is what gives someone that list.
  { key: 'mechanic',  label: 'Mechanic',  hint: 'The shop — the weekly checklist, daily and monthly checks, parts requests, broken gear.' },
  { key: 'crew',      label: 'Employee',  hint: 'The crew app — routes, clock, pay, availability.' },
];
const OLD_INSPECTOR = { key: 'inspector', label: 'Inspector (old)', hint: 'The old lawn-checks-only hat. Manager does this now — tick Manager to move them.' };
const HAT_LABEL: Record<string, string> = Object.fromEntries([...HATS, OLD_INSPECTOR].map(h => [h.key, h.label]));

function formatAvailDays(days: string) {
  const nums = (days || '1,2,3,4,5').split(',').map(Number).filter(n => !isNaN(n));
  if (nums.length === 7) return 'Every day';
  if (JSON.stringify(nums) === JSON.stringify([1,2,3,4,5])) return 'Mon–Fri';
  if (JSON.stringify(nums) === JSON.stringify([1,2,3,4,5,6])) return 'Mon–Sat';
  return nums.map(n => DAY_LABELS[n]).join(', ');
}

function fmt12(t: string | null | undefined) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')}${ampm}`;
}

function formatBlockRange(b: any) {
  const start = b.start_date;
  const end = b.end_date && b.end_date !== b.start_date ? ` – ${b.end_date}` : '';
  const time = b.start_time ? ` · ${fmt12(b.start_time)}–${fmt12(b.end_time || '')}` : ' · All day';
  return `${start}${end}${time}`;
}

const emptyForm = {
  name: '', phone: '', pin: '', hourly_rate: '18', role: 'crew', hats: ['crew'] as string[], ghost: false,
  // They make their own PIN from a texted link (owner, Sep 29 2026).
  invite: true,
  can_receive_shift_texts: true, work_preferences: '', venmo_handle: '',
};

export default function Employees() {
  const readOnly = useReadOnly();   // view-only login: grey out what can only ever be refused
  const qc = useQueryClient();
  const [showPins, setShowPins] = useState(false);
  const [search, setSearch] = useState('');
  const [profileEmp, setProfileEmp] = useState<any | null>(null);
  const [addModal, setAddModal] = useState(false);
  const [importModal, setImportModal] = useState(false);
  const [editModal, setEditModal] = useState<any | null>(null);
  const [pinModal, setPinModal] = useState<any | null>(null);
  const [availModal, setAvailModal] = useState<any | null>(null);
  const [form, setForm] = useState<any>(emptyForm);
  const [editForm, setEditForm] = useState<any>({});
  const [newPin, setNewPin] = useState('');
  // The join link to hand a new person: { url, name, phone, days }.
  const [shareLink, setShareLink] = useState<any | null>(null);
  const makeLink = useMutation((emp: any) => api.post(`/employees/${emp.id}/invite`).then(r => ({ ...r.data.invite, phone: emp.phone })), {
    onSuccess: (inv: any) => setShareLink(inv),
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not make a link'); },
  });

  // "Show PINs": the owner can reveal the login codes; the server only hands
  // them to the admin role and logs the reveal. Everyone else keeps ••••••.
  const { data: revealed } = useQuery('users-revealed', () => api.get('/auth/users', { params: { reveal: 1 } }).then(r => r.data.users as any[]), { enabled: showPins, staleTime: 60_000 });
  const pinByMember: Record<number, string> = {};
  for (const u of revealed || []) if (u.crew_member_id && u.pin) pinByMember[Number(u.crew_member_id)] = String(u.pin);
  const [tidyOpen, setTidyOpen] = useState(false);
  const [pinLinksOpen, setPinLinksOpen] = useState(false);
  const { loginWithToken, user } = useAuth();
  const { data, isLoading } = useQuery('employees', () =>
    api.get('/employees').then(r => r.data.employees as any[])
  );
  const createEmployee = useMutation(async () => {
    const invite = form.invite && !form.ghost;
    if (!invite && !/^\d{6}$/.test(form.pin)) throw new Error('PIN must be exactly 6 digits');
    if (form.ghost) {
      const r = await api.post('/auth/create-crew-viewer', { pin: form.pin });
      return r.data?.message || 'Ghost crew login ready';
    }
    if (!form.name.trim()) throw new Error('Name is required');
    if (!form.hats.length) throw new Error('Pick at least one app they can open');
    const r = await api.post('/employees', { ...form, invite, pin: invite ? undefined : form.pin, hourly_rate: form.hourly_rate === '' ? undefined : Number(form.hourly_rate) });
    if (r.data?.invite) setShareLink({ ...r.data.invite, phone: form.phone });
    return `${form.name.trim()} added — can open ${form.hats.map(h => HAT_LABEL[h]).join(', ')}${invite ? ' · send them the link to make their PIN' : ''}`;
  }, {
    onSuccess: (msg: any) => {
      qc.invalidateQueries('employees');
      qc.invalidateQueries('users-plain');
      qc.invalidateQueries('users-revealed');
      toast.success(typeof msg === 'string' ? msg : 'Created');
      setAddModal(false);
      setForm(emptyForm);
    },
    onError: (e: any) => { toast.error(e.response?.data?.message || e.message || 'Failed'); },
  });

  const updateEmployee = useMutation(async () => {
    const { hats, newPin, ...rest } = editForm;
    await api.put(`/employees/${editModal.id}`, { ...rest, hourly_rate: rest.hourly_rate !== '' ? Number(rest.hourly_rate) : 0 });
    // The apps they can open live on the profile too (owner, Sep 18 2026).
    const before = (editModal.hats || []).join(',');
    if ((hats || []).join(',') !== before || (!editModal.login_id && newPin)) {
      const r = await api.put(`/employees/${editModal.id}/access`, { hats, ...(newPin ? { pin: newPin } : {}) });
      return r.data?.message || 'Employee updated';
    }
    return 'Employee updated';
  }, {
    onSuccess: (msg: any) => {
      qc.invalidateQueries('employees');
      qc.invalidateQueries('users-revealed');
      toast.success(typeof msg === 'string' ? msg : 'Employee updated');
      setEditModal(null);
    },
    onError: (e: any) => { toast.error(e.response?.data?.message || e.message || 'Failed to update'); },
  });

  // Two profiles for one human: fold this one into the other. If the login
  // doing it is the one folded, the server hands back a token for the kept
  // login so nothing signs out.
  const [foldInto, setFoldInto] = useState<string>('');
  const foldEmployee = useMutation(async () => {
    const r = await api.post(`/employees/${editModal.id}/fold`, { into_id: Number(foldInto) });
    return r.data;
  }, {
    onSuccess: (d: any) => {
      if (d.token && d.user) loginWithToken(d.token, d.user);
      qc.invalidateQueries('employees');
      qc.invalidateQueries('users-revealed');
      toast.success(d.message || 'Folded in', { autoClose: 12000 });
      setEditModal(null); setFoldInto('');
    },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not fold them in'); },
  });

  const deactivateEmployee = useMutation(async (id: number) => {
    await api.delete(`/employees/${id}`);
  }, {
    onSuccess: () => { qc.invalidateQueries('employees'); toast.success('Employee deactivated'); },
  });

  const changePin = useMutation(async () => {
    if (!/^\d{6}$/.test(newPin)) throw new Error('PIN must be exactly 6 digits');
    await api.put(`/employees/${pinModal.id}/pin`, { pin: newPin });
  }, {
    onSuccess: () => {
      qc.invalidateQueries('employees');
      toast.success('PIN updated');
      setPinModal(null);
      setNewPin('');
    },
    onError: (e: any) => { toast.error(e.response?.data?.message || e.message || 'Failed'); },
  });

  function openEdit(emp: any) {
    setEditModal(emp);
    setEditForm({
      name: emp.name,
      phone: emp.phone || '',
      hourly_rate: emp.hourly_rate ?? '',
      role: emp.role || 'crew',
      hats: (emp.hats && emp.hats.length ? emp.hats : []) as string[],
      newPin: '',
      can_receive_shift_texts: emp.can_receive_shift_texts !== 0,
      work_preferences: emp.work_preferences || '',
      venmo_handle: emp.venmo_handle || '',
      availability_days: emp.availability_days || '1,2,3,4,5',
      availability_start: emp.availability_start || '06:00',
      availability_end: emp.availability_end || '20:00',
    });
  }

  const employees = data || [];
  const q = search.trim().toLowerCase();
  const visible = q
    ? employees.filter((e: any) => [e.name, e.phone, ...(e.hat_words || []), e.venmo_handle]
        .filter(Boolean).some((v: any) => String(v).toLowerCase().includes(q)))
    : employees;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-gray-900">Employees</h1>
          <p className="text-sm text-gray-500 mt-0.5">{employees.length} active employee{employees.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <div className="relative">
            <Search className="h-4 w-4 text-gray-500 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search employees…"
              className="input pl-8 py-2 text-sm w-44 sm:w-56" />
          </div>
          {/* Owner only: reveals the codes (the server refuses everyone else). */}
          <button onClick={() => setShowPins(!showPins)} title="Owner only — each reveal is logged under Settings → Security"
            className="flex items-center gap-1.5 text-sm text-gray-500 border border-gray-300 rounded-lg px-3 py-2 hover:bg-gray-50">
            {showPins ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            {showPins ? 'Hide' : 'Show'} PINs
          </button>
          <button onClick={() => setImportModal(true)}
            className="flex items-center gap-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg px-3 py-2 hover:bg-gray-50">
            <Upload className="h-4 w-4" /> Import
          </button>
          {/* Owner only: reset PINs and text make-your-own-PIN links to the people he ticks. */}
          {user?.role === 'admin' && (
            <button onClick={() => setPinLinksOpen(true)} title="Reset someone's PIN and text them a link to make their own"
              className="flex items-center gap-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg px-3 py-2 hover:bg-gray-50">
              <KeyRound className="h-4 w-4" /> Sign-in links
            </button>
          )}
          <button onClick={() => setTidyOpen(true)} title="Doubles, doubled logins, and anyone with no work in three weeks"
            className="flex items-center gap-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg px-3 py-2 hover:bg-gray-50">
            <Sparkles className="h-4 w-4" /> Tidy up
          </button>
          <button onClick={() => { setForm(emptyForm); setAddModal(true); }} className="btn-primary">
            <Plus className="h-4 w-4 mr-1" /> Add Employee
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center text-gray-500 py-10">Loading…</div>
      ) : employees.length === 0 ? (
        <div className="card p-8 text-center text-gray-500">No employees yet.</div>
      ) : (
        <div className="card overflow-hidden">
          <div className="hidden md:grid grid-cols-[1.4fr_5rem_8rem_11rem_6rem_auto] gap-3 px-4 py-2 bg-gray-50 border-b border-gray-200 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
            <span>Name</span><span>Rate</span><span>Phone</span><span>Availability</span><span>Last worked</span><span className="text-right">Actions</span>
          </div>
          <ul className="divide-y divide-gray-100">
            {visible.length === 0 && <li className="px-4 py-6 text-sm text-gray-500 text-center">No one matches "{search}".</li>}
            {visible.map((emp: any) => (
              <EmployeeRow key={emp.id} emp={emp} revealedPin={showPins ? pinByMember[emp.id] : undefined}
                onOpenProfile={() => setProfileEmp(emp)}
                onEdit={() => openEdit(emp)}
                onDeactivate={() => { if (window.confirm(`Deactivate ${emp.name}?`)) deactivateEmployee.mutate(emp.id); }}
                onChangePin={() => { setPinModal(emp); setNewPin(''); }}
                onManageAvailability={() => setAvailModal(emp)}
              />
            ))}
          </ul>
        </div>
      )}

      {tidyOpen && <TidyModal onClose={() => setTidyOpen(false)} onDone={() => { qc.invalidateQueries('employees'); qc.invalidateQueries('users-plain'); }} />}
      {pinLinksOpen && <PinLinksModal onClose={() => setPinLinksOpen(false)} onDone={() => { qc.invalidateQueries('employees'); qc.invalidateQueries('users-revealed'); }} />}


      {/* Add Employee Modal */}
      {shareLink && <JoinLinkModal invite={shareLink} onClose={() => setShareLink(null)} />}
      {addModal && (
        <SimpleModal title="Add Employee" onClose={() => setAddModal(false)}>
          <div className="space-y-3">
            {!form.ghost && (
            <div>
              <label className="label-sm">Name *</label>
              <input value={form.name} onChange={e => setForm((f: any) => ({ ...f, name: e.target.value }))}
                className="input" placeholder="First Last" autoFocus />
            </div>
            )}
            <div>
              <label className="label-sm">Phone</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <input type="tel" value={form.phone} onChange={e => setForm((f: any) => ({ ...f, phone: e.target.value }))}
                  className="input pl-8" placeholder="(512) 555-0100" />
              </div>
            </div>
            {!form.ghost && (
            <div>
              <label className="label-sm">Venmo handle <span className="text-gray-500 font-normal">(for one-tap pay)</span></label>
              <div className="relative">
                <Wallet className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <input value={form.venmo_handle} onChange={e => setForm((f: any) => ({ ...f, venmo_handle: e.target.value }))}
                  className="input pl-8" placeholder="@Their-Venmo" />
              </div>
            </div>
            )}
            {!form.ghost && (
              <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                <input type="checkbox" className="mt-0.5" checked={form.invite} onChange={e => setForm((f: any) => ({ ...f, invite: e.target.checked }))} />
                <span>They make their own PIN <span className="block text-xs text-gray-500">You get a link to text them. They pick a PIN and check their phone, email and Venmo.</span></span>
              </label>
            )}
            {(form.ghost || !form.invite) && (
            <div>
              <label className="label-sm">6-Digit Login PIN *</label>
              <div className="relative">
                <Hash className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <input type="tel" inputMode="numeric" maxLength={6} value={form.pin}
                  onChange={e => setForm((f: any) => ({ ...f, pin: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                  className="input pl-8 font-mono tracking-widest" placeholder="e.g. 123456" />
              </div>
              <p className="text-xs text-gray-500 mt-0.5">Must be unique across all logins.</p>
            </div>
            )}
            {!form.ghost && (<>
            <HatPicker value={form.hats} onChange={hats => setForm((f: any) => ({ ...f, hats }))} />
            <div>
              <label className="label-sm">Base pay ($/hr)</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <input type="number" min="0" step="0.25" value={form.hourly_rate}
                  onChange={e => setForm((f: any) => ({ ...f, hourly_rate: e.target.value }))}
                  className="input pl-8" placeholder="18.00" />
              </div>
              <p className="text-xs text-gray-500 mt-0.5">Everyone starts at $18. Change it here or on their profile any time.</p>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" checked={form.can_receive_shift_texts}
                onChange={e => setForm((f: any) => ({ ...f, can_receive_shift_texts: e.target.checked }))}
                className="rounded text-brand-600" />
              Can receive open-shift texts
            </label>
            </>)}
          </div>
          <div className="flex justify-between items-center gap-3 mt-4">
            <button type="button" onClick={() => setForm((f: any) => ({ ...f, ghost: !f.ghost }))} className="text-[11px] text-gray-500 hover:text-gray-600 underline">
              {form.ghost ? 'Back to a person' : 'Ghost crew look-only login instead'}
            </button>
            <div className="flex gap-3">
              <button onClick={() => setAddModal(false)} className="btn-secondary">Cancel</button>
              <button onClick={() => createEmployee.mutate()} disabled={readOnly || (createEmployee.isLoading)} className="btn-primary">
                {createEmployee.isLoading ? 'Creating…' : (form.ghost ? 'Create ghost login' : 'Add Employee')}
              </button>
            </div>
          </div>
        </SimpleModal>
      )}

      {/* Edit Employee Modal */}
      {editModal && (
        <SimpleModal title={`Edit — ${editModal.name}`} onClose={() => setEditModal(null)}>
          <div className="space-y-3">
            <div>
              <label className="label-sm">Name</label>
              <input value={editForm.name} onChange={e => setEditForm((f: any) => ({ ...f, name: e.target.value }))}
                className="input" />
            </div>
            <div>
              <label className="label-sm">Phone</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <input type="tel" value={editForm.phone} onChange={e => setEditForm((f: any) => ({ ...f, phone: e.target.value }))}
                  className="input pl-8" />
              </div>
            </div>
            <div>
              <label className="label-sm">Venmo handle <span className="text-gray-500 font-normal">(for one-tap pay)</span></label>
              <div className="relative">
                <Wallet className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <input value={editForm.venmo_handle || ''} onChange={e => setEditForm((f: any) => ({ ...f, venmo_handle: e.target.value }))}
                  className="input pl-8" placeholder="@Their-Venmo" />
              </div>
            </div>
            <div>
              <label className="label-sm">Base pay ($/hr)</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <input type="number" min="0" step="0.25" value={editForm.hourly_rate}
                  onChange={e => setEditForm((f: any) => ({ ...f, hourly_rate: e.target.value }))}
                  className="input pl-8" />
              </div>
            </div>

            <div className="border-t border-gray-100 pt-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Login &amp; apps</p>
              {editModal.login_id ? (
                <p className="text-xs text-gray-500 mb-2">PIN {editModal.has_pin ? 'set' : 'not set'} · <button type="button" onClick={() => { setPinModal(editModal); setNewPin(''); }} className="text-brand-600 underline">change PIN</button></p>
              ) : (
                <div className="mb-2">
                  <label className="label-sm">6-digit PIN to make their login</label>
                  <input type="tel" inputMode="numeric" maxLength={6} value={editForm.newPin || ''} onChange={e => setEditForm((f: any) => ({ ...f, newPin: e.target.value.replace(/\D/g, '').slice(0, 6) }))} className="input font-mono tracking-widest" placeholder="e.g. 123456" />
                </div>
              )}
              <HatPicker value={editForm.hats || []} onChange={hats => setEditForm((f: any) => ({ ...f, hats }))} />
              {!readOnly && (
                <button type="button" onClick={() => makeLink.mutate(editModal)} disabled={makeLink.isLoading}
                  className="mt-2 text-xs font-semibold text-brand-700 underline disabled:opacity-50">
                  {editModal.has_pin ? 'Send them a link to change their PIN and profile' : 'Send them a link to make their PIN'}
                </button>
              )}
            </div>

            <div className="border-t border-gray-100 pt-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Two profiles for one person?</p>
              <p className="text-xs text-gray-500 mb-2">Fold this one into the other. Jobs, pay, clocks and the login all move over, and the two logins become one with every app both could open.</p>
              <div className="flex gap-2">
                <select value={foldInto} onChange={e => setFoldInto(e.target.value)} className="input text-sm flex-1">
                  <option value="">Fold {editModal.name} into…</option>
                  {employees.filter((e: any) => e.id !== editModal.id).map((e: any) => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
                <button type="button" disabled={readOnly || (!foldInto || foldEmployee.isLoading)}
                  onClick={() => { const into = employees.find((e: any) => String(e.id) === foldInto); if (window.confirm(`Fold ${editModal.name} into ${into?.name}? ${editModal.name} goes inactive and everything moves to ${into?.name}.`)) foldEmployee.mutate(); }}
                  className="px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-800 hover:bg-gray-50 disabled:opacity-50">{foldEmployee.isLoading ? 'Folding…' : 'Fold in'}</button>
              </div>
            </div>

            <div className="border-t border-gray-100 pt-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Recurring Availability</p>
              <div className="flex gap-1.5 flex-wrap">
                {DAY_LABELS.map((d, i) => {
                  const days = (editForm.availability_days || '').split(',').map(Number).filter((n: number) => !isNaN(n));
                  const checked = days.includes(i);
                  return (
                    <button key={d} type="button"
                      onClick={() => {
                        const updated = checked ? days.filter((x: number) => x !== i) : [...days, i];
                        setEditForm((f: any) => ({ ...f, availability_days: updated.sort((a: number, b: number) => a - b).join(',') }));
                      }}
                      className={`press px-2.5 py-1 text-xs rounded-full border font-medium transition-colors ${checked ? 'bg-brand-600 text-black border-brand-600' : 'bg-white text-gray-600 border-gray-300'}`}>
                      {d}
                    </button>
                  );
                })}
              </div>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div>
                  <label className="label-sm">From</label>
                  <input type="time" value={editForm.availability_start || '06:00'}
                    onChange={e => setEditForm((f: any) => ({ ...f, availability_start: e.target.value }))}
                    className="input" />
                </div>
                <div>
                  <label className="label-sm">Until</label>
                  <input type="time" value={editForm.availability_end || '20:00'}
                    onChange={e => setEditForm((f: any) => ({ ...f, availability_end: e.target.value }))}
                    className="input" />
                </div>
              </div>
            </div>

            <div>
              <label className="label-sm">Work Preferences</label>
              <textarea value={editForm.work_preferences}
                onChange={e => setEditForm((f: any) => ({ ...f, work_preferences: e.target.value }))}
                className="input" rows={2} placeholder="e.g. prefers mornings" />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" checked={editForm.can_receive_shift_texts}
                onChange={e => setEditForm((f: any) => ({ ...f, can_receive_shift_texts: e.target.checked }))}
                className="rounded text-brand-600" />
              Can receive open-shift texts
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" checked={editForm.is_active !== false}
                onChange={e => setEditForm((f: any) => ({ ...f, is_active: e.target.checked }))}
                className="rounded text-brand-600" />
              <UserCheck className="h-4 w-4 text-gray-500" />
              Active
            </label>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <button onClick={() => setEditModal(null)} className="btn-secondary">Cancel</button>
            <button onClick={() => updateEmployee.mutate()} disabled={readOnly || (updateEmployee.isLoading)} className="btn-primary">
              {updateEmployee.isLoading ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </SimpleModal>
      )}

      {/* Change PIN Modal */}
      {pinModal && (
        <SimpleModal title={`Change PIN — ${pinModal.name}`} onClose={() => { setPinModal(null); setNewPin(''); }}>
          <div>
            <label className="label-sm">New 6-Digit PIN *</label>
            <div className="relative">
              <Hash className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
              <input type="tel" inputMode="numeric" maxLength={6} value={newPin}
                onChange={e => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="input pl-8 font-mono tracking-widest text-xl text-center" placeholder="——————"
                autoFocus />
            </div>
            <p className="text-xs text-gray-500 mt-1">Must be unique. Employee uses this to sign in on mobile.</p>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <button onClick={() => { setPinModal(null); setNewPin(''); }} className="btn-secondary">Cancel</button>
            <button onClick={() => changePin.mutate()} disabled={readOnly || (changePin.isLoading || newPin.length !== 6)}
              className="btn-primary">
              {changePin.isLoading ? 'Saving…' : 'Save PIN'}
            </button>
          </div>
        </SimpleModal>
      )}

      {/* Availability Management Modal */}
      {availModal && (
        <AvailabilityModal emp={availModal} onClose={() => setAvailModal(null)} />
      )}

      {/* Employee profile — what they have actually done */}
      {profileEmp && (
        <EmployeeProfileModal emp={profileEmp} onClose={() => setProfileEmp(null)} onEdit={() => { setProfileEmp(null); openEdit(profileEmp); }} />
      )}

      {/* Import Employees Modal */}
      {importModal && (
        <EmployeeImportModal
          onClose={() => setImportModal(false)}
          onImported={() => qc.invalidateQueries('employees')}
        />
      )}
    </div>
  );
}

// ── Employee row (list, not cards) ─────────────────────────────────────────────
function EmployeeRow({ emp, revealedPin, onOpenProfile, onEdit, onDeactivate, onChangePin, onManageAvailability }: {
  emp: any; revealedPin?: string; onOpenProfile: () => void; onEdit: () => void; onDeactivate: () => void; onChangePin: () => void; onManageAvailability: () => void;
}) {
  const readOnly = useReadOnly();   // view-only login: grey out what can only ever be refused
  const availDays = formatAvailDays(emp.availability_days || '1,2,3,4,5');
  const availTime = `${fmt12(emp.availability_start || '06:00')} – ${fmt12(emp.availability_end || '20:00')}`;
  const lastWorked = emp.last_assigned_date
    ? new Date(String(emp.last_assigned_date).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '—';

  return (
    <li className="px-4 py-3 grid grid-cols-1 md:grid-cols-[1.4fr_5rem_8rem_11rem_6rem_auto] gap-x-3 gap-y-1.5 items-center hover:bg-gray-50">
      <div className="flex items-center gap-3 min-w-0">
        <div className="h-9 w-9 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0">
          <span className="text-sm font-bold text-brand-700">{emp.name.charAt(0)}</span>
        </div>
        <div className="min-w-0">
          <button onClick={onOpenProfile} className="font-semibold text-gray-900 truncate hover:text-brand-600 text-left" title="Open profile — jobs, hours, efficiency, pay">{emp.name}</button>
          <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
            {emp.venmo_handle && <span className="text-[11px] text-sky-700 bg-sky-50 px-1.5 py-0.5 rounded">@{String(emp.venmo_handle).replace(/^@/, '')}</span>}
            {(emp.hats || []).map((h: string) => (
              <span key={h} className={`text-[11px] px-1.5 py-0.5 rounded ${h === 'admin' ? 'bg-brand-100 text-brand-700' : 'bg-gray-100 text-gray-600'}`}>{HAT_LABEL[h] || h}</span>
            ))}
            {!emp.login_id && <span className="text-[11px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">no login</span>}
            <span className="text-[11px] font-mono tracking-widest text-gray-500 flex items-center gap-1">
              <Hash className="h-3 w-3 text-gray-500" />{emp.has_pin ? (revealedPin || '••••••') : 'no PIN'}
              <button onClick={onChangePin} className="text-brand-600 hover:text-brand-800 underline font-sans tracking-normal">change</button>
            </span>
            {emp.can_receive_shift_texts ? null : <span className="text-[11px] text-gray-500 flex items-center gap-1"><MessageSquare className="h-3 w-3" /> no shift texts</span>}
          </div>
        </div>
      </div>
      <div className="text-sm text-green-700 font-semibold flex items-center gap-0.5">
        {emp.hourly_rate > 0 ? <><DollarSign className="h-3.5 w-3.5" />{Number(emp.hourly_rate).toFixed(2)}/hr</> : <span className="text-gray-500 font-normal">—</span>}
      </div>
      <div className="text-xs text-gray-600 flex items-center gap-1.5">
        <Phone className="h-3 w-3 text-gray-500" />{emp.phone || <span className="text-gray-500 italic">no phone</span>}
      </div>
      <div className="text-xs text-gray-600">
        <span className="flex items-center gap-1.5"><Calendar className="h-3 w-3 text-gray-500" />{availDays}</span>
        <span className="flex items-center gap-1.5"><Clock className="h-3 w-3 text-gray-500" />{availTime}</span>
      </div>
      <div className="text-xs text-gray-600">{lastWorked}</div>
      <div className="flex items-center gap-1 md:justify-end">
        <button onClick={onManageAvailability} className="text-xs text-brand-600 hover:text-brand-800 font-medium border border-brand-200 rounded-md px-2 py-1">Blocks</button>
        <button onClick={onEdit} className="p-1.5 text-gray-500 hover:text-brand-600 rounded" title="Edit"><Edit2 className="h-4 w-4" /></button>
        <button onClick={onDeactivate} disabled={readOnly} className="p-1.5 text-gray-500 hover:text-red-500 rounded disabled:opacity-40" title="Deactivate"><UserX className="h-4 w-4" /></button>
      </div>
    </li>
  );
}

// ── Availability Management Modal ──────────────────────────────────────────────
function AvailabilityModal({ emp, onClose }: { emp: any; onClose: () => void }) {
  const readOnly = useReadOnly();   // view-only login: grey out what can only ever be refused
  const qc = useQueryClient();
  const [addForm, setAddForm] = useState({ start_date: '', end_date: '', start_time: '', end_time: '', note: '' });
  const [showAdd, setShowAdd] = useState(false);

  const { data, isLoading, refetch } = useQuery(
    ['availability-blocks', emp.id],
    () => api.get(`/availability/${emp.id}/blocks`).then(r => r.data.blocks as any[]),
    { refetchOnWindowFocus: false }
  );

  const addBlock = useMutation(async () => {
    if (!addForm.start_date) throw new Error('Start date required');
    await api.post(`/availability/${emp.id}/blocks`, {
      start_date: addForm.start_date,
      end_date: addForm.end_date || null,
      start_time: addForm.start_time || null,
      end_time: addForm.end_time || null,
      note: addForm.note || null,
    });
  }, {
    onSuccess: () => {
      refetch();
      qc.invalidateQueries('employees');
      setAddForm({ start_date: '', end_date: '', start_time: '', end_time: '', note: '' });
      setShowAdd(false);
      toast.success('Block added');
    },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Failed'); },
  });

  const deleteBlock = useMutation(async (blockId: number) => {
    await api.delete(`/availability/${emp.id}/blocks/${blockId}`);
  }, {
    onSuccess: () => { refetch(); toast.success('Block removed'); },
    onError: () => { toast.error('Failed to remove block'); },
  });

  const blocks: any[] = data || [];
  const today = businessToday();
  const upcoming = blocks.filter(b => (b.end_date || b.start_date) >= today);
  const past = blocks.filter(b => (b.end_date || b.start_date) < today);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <div>
            <h3 className="text-base font-black uppercase tracking-tight text-gray-900">Unavailability — {emp.name}</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Available: {formatAvailDays(emp.availability_days)} · {fmt12(emp.availability_start || '06:00')} – {fmt12(emp.availability_end || '20:00')}
            </p>
          </div>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-500 hover:text-gray-600" /></button>
        </div>
        <div className="px-5 py-4 space-y-4">
          {/* Upcoming blocks */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Upcoming Unavailability</p>
              <button onClick={() => setShowAdd(v => !v)}
                className="flex items-center gap-1 text-xs text-brand-600 hover:text-brand-800 font-medium">
                <Plus className="h-3.5 w-3.5" /> Add Block
              </button>
            </div>

            {showAdd && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="label-sm">Start date *</label>
                    <input type="date" value={addForm.start_date}
                      onChange={e => setAddForm(f => ({ ...f, start_date: e.target.value }))}
                      className="input" />
                  </div>
                  <div>
                    <label className="label-sm">End date (range)</label>
                    <input type="date" value={addForm.end_date}
                      min={addForm.start_date}
                      onChange={e => setAddForm(f => ({ ...f, end_date: e.target.value }))}
                      className="input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="label-sm">Start time (optional)</label>
                    <input type="time" value={addForm.start_time}
                      onChange={e => setAddForm(f => ({ ...f, start_time: e.target.value }))}
                      className="input" />
                  </div>
                  <div>
                    <label className="label-sm">End time</label>
                    <input type="time" value={addForm.end_time}
                      onChange={e => setAddForm(f => ({ ...f, end_time: e.target.value }))}
                      className="input" />
                  </div>
                </div>
                <div>
                  <label className="label-sm">Note / Reason (optional)</label>
                  <input value={addForm.note}
                    onChange={e => setAddForm(f => ({ ...f, note: e.target.value }))}
                    className="input" placeholder="e.g. Vacation, Doctor appointment" />
                </div>
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setShowAdd(false)} className="btn-secondary text-xs py-1.5 px-3">Cancel</button>
                  <button onClick={() => addBlock.mutate()} disabled={readOnly || (addBlock.isLoading || !addForm.start_date)}
                    className="btn-primary text-xs py-1.5 px-3">
                    {addBlock.isLoading ? 'Adding…' : 'Add Block'}
                  </button>
                </div>
              </div>
            )}

            {isLoading ? (
              <div className="text-xs text-gray-500 py-2">Loading…</div>
            ) : upcoming.length === 0 ? (
              <div className="text-xs text-gray-500 py-2 italic">No upcoming unavailability blocks.</div>
            ) : (
              <div className="space-y-2">
                {upcoming.map((b: any) => (
                  <div key={b.id} className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    <AlertTriangle className="h-3.5 w-3.5 text-red-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-red-800">{formatBlockRange(b)}</p>
                      {b.note && <p className="text-xs text-red-600 mt-0.5">{b.note}</p>}
                    </div>
                    <button disabled={readOnly} onClick={() => { if (window.confirm('Remove this block?')) deleteBlock.mutate(b.id); }}
                      className="text-red-400 hover:text-red-600 flex-shrink-0">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Past blocks */}
          {past.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Past</p>
              <div className="space-y-1.5">
                {past.map((b: any) => (
                  <div key={b.id} className="flex items-center gap-2 text-gray-500 text-xs px-3 py-1.5 bg-gray-50 rounded-lg">
                    <span className="flex-1">{formatBlockRange(b)}{b.note ? ` · ${b.note}` : ''}</span>
                    <button disabled={readOnly} onClick={() => deleteBlock.mutate(b.id)} className="hover:text-red-400">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Employee CSV Import Modal ──────────────────────────────────────────────────
function EmployeeImportModal({ onClose, onImported }: { onClose: () => void; onImported: () => void }) {
  const [step, setStep] = useState<'upload' | 'preview' | 'done'>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<any>(null);
  const [result, setResult] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'create' | 'update' | 'invalid'>('create');
  const [error, setError] = useState('');

  function downloadExample() {
    const csv = [
      'First Name,Last Name,Phone,Email,PIN,Role,Hourly Rate,Active,Can Receive Push Notifications,Device Type,Push Token,Notification Preference,Availability Notes,Notes',
      'Jonathan,Smith,5125551111,jonathan@example.com,123123,Crew Member,22,Yes,Yes,iPhone,,Push + SMS,Weekdays and Saturdays,Main mowing helper',
      'Maddox,Morgan,5125552222,maddox@example.com,222222,Crew Leader,26,Yes,Yes,iPhone,,Push + SMS,Available mornings,Crew lead option',
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'employee-import-example.csv'; a.click();
    URL.revokeObjectURL(url);
  }

  async function runDryRun() {
    if (!file) { setError('Please select a CSV file'); return; }
    setLoading(true); setError('');
    try {
      const fd = new FormData();
      fd.append('csvFile', file);
      const { data } = await api.post('/employees/import/dry-run', fd);
      setPreview(data);
      const tab = data.toCreate.length ? 'create' : data.toUpdate.length ? 'update' : 'invalid';
      setActiveTab(tab as any);
      setStep('preview');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to process CSV');
    } finally {
      setLoading(false);
    }
  }

  async function applyImport() {
    if (!preview) return;
    setLoading(true); setError('');
    try {
      const { data } = await api.post('/employees/import/apply', {
        toCreate: preview.toCreate,
        toUpdate: preview.toUpdate,
      });
      setResult(data);
      setStep('done');
      onImported();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Import failed');
    } finally {
      setLoading(false);
    }
  }

  const totalToImport = preview ? preview.toCreate.length + preview.toUpdate.length : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
          <div>
            <h3 className="text-base font-black uppercase tracking-tight text-gray-900">
              {step === 'upload' ? 'Import Employees' : step === 'preview' ? 'Import Preview' : 'Import Complete'}
            </h3>
            {step === 'preview' && preview && (
              <p className="text-xs text-gray-500 mt-0.5">
                {preview.toCreate.length} new · {preview.toUpdate.length} updates · {preview.invalid.length} invalid
              </p>
            )}
          </div>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-500 hover:text-gray-600" /></button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">

          {/* Step 1: Upload */}
          {step === 'upload' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">Upload a CSV to bulk create or update employees. Matching uses phone → email → full name.</p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-1.5">
                <p className="text-xs font-semibold text-blue-800">Required CSV columns</p>
                <p className="text-xs text-blue-700 font-mono leading-relaxed">
                  First Name, Last Name, Phone, Email, PIN, Role, Hourly Rate, Active,<br />
                  Can Receive Push Notifications, Device Type, Push Token,<br />
                  Notification Preference, Availability Notes, Notes
                </p>
                <button onClick={downloadExample}
                  className="flex items-center gap-1.5 text-xs text-blue-700 font-medium hover:text-blue-900 underline mt-1">
                  <FileText className="h-3.5 w-3.5" /> Download CSV example
                </button>
              </div>
              <div className="space-y-1">
                <label className="label-sm">CSV File</label>
                <input
                  type="file" accept=".csv,text/csv"
                  onChange={e => { setFile(e.target.files?.[0] || null); setError(''); }}
                  className="block w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100 cursor-pointer"
                />
                {file && <p className="text-xs text-gray-500 mt-0.5">{file.name}</p>}
              </div>
              <div className="text-xs text-gray-500 space-y-0.5">
                <p>• Blank PIN → 6-digit PIN auto-generated and shown in preview</p>
                <p>• Blank Hourly Rate → role default (Laborer $18, Crew Member $22, Crew Leader $26, PM $32)</p>
                <p>• Existing employees are updated only where CSV has a value — never duplicated</p>
              </div>
              {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
            </div>
          )}

          {/* Step 2: Preview */}
          {step === 'preview' && preview && (
            <div className="space-y-3">
              <div className="flex gap-0 border-b border-gray-200">
                {[
                  { key: 'create', label: `New (${preview.toCreate.length})` },
                  { key: 'update', label: `Updates (${preview.toUpdate.length})` },
                  { key: 'invalid', label: `Invalid (${preview.invalid.length})`, warn: preview.invalid.length > 0 },
                ].map(({ key, label, warn }) => (
                  <button key={key} onClick={() => setActiveTab(key as any)}
                    className={`px-4 py-2 text-xs font-semibold border-b-2 -mb-px transition-colors ${activeTab === key
                      ? (warn ? 'border-red-500 text-red-700' : 'border-brand-600 text-brand-700')
                      : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                    {label}
                  </button>
                ))}
              </div>

              {activeTab === 'create' && (
                preview.toCreate.length === 0
                  ? <p className="text-sm text-gray-500 py-6 text-center italic">No new employees to create.</p>
                  : <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead><tr className="text-left text-gray-500 border-b border-gray-100">
                        <th className="py-2 pr-3 font-semibold">Name</th>
                        <th className="py-2 pr-3 font-semibold">Phone</th>
                        <th className="py-2 pr-3 font-semibold">Role</th>
                        <th className="py-2 pr-3 font-semibold">Rate</th>
                        <th className="py-2 font-semibold">PIN</th>
                      </tr></thead>
                      <tbody>
                        {preview.toCreate.map((r: any) => (
                          <tr key={r.rowNum} className="border-b border-gray-50">
                            <td className="py-1.5 pr-3 font-medium text-gray-900">{r.name}</td>
                            <td className="py-1.5 pr-3 text-gray-600">{r.phone || '—'}</td>
                            <td className="py-1.5 pr-3 text-gray-600">${Number(r.hourly_rate).toFixed(2)}/hr</td>
                            <td className="py-1.5">
                              <span className="font-mono">{r.pin}</span>
                              {r.generatedPin && (
                                <span className="ml-1.5 text-yellow-700 bg-yellow-100 text-xs px-1.5 rounded-full">auto</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
              )}

              {activeTab === 'update' && (
                preview.toUpdate.length === 0
                  ? <p className="text-sm text-gray-500 py-6 text-center italic">No existing employees to update.</p>
                  : <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead><tr className="text-left text-gray-500 border-b border-gray-100">
                        <th className="py-2 pr-3 font-semibold">Existing Employee</th>
                        <th className="py-2 pr-3 font-semibold">CSV Name</th>
                        <th className="py-2 pr-3 font-semibold">Phone</th>
                        <th className="py-2 pr-3 font-semibold">Role</th>
                        <th className="py-2 font-semibold">Rate</th>
                      </tr></thead>
                      <tbody>
                        {preview.toUpdate.map((r: any) => (
                          <tr key={r.rowNum} className="border-b border-gray-50">
                            <td className="py-1.5 pr-3 text-gray-500 italic">{r.existingName}</td>
                            <td className="py-1.5 pr-3 font-medium text-gray-900">{r.name}</td>
                            <td className="py-1.5 pr-3 text-gray-600">{r.phone || '—'}</td>
                            <td className="py-1.5 text-gray-600">${Number(r.hourly_rate).toFixed(2)}/hr</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
              )}

              {activeTab === 'invalid' && (
                preview.invalid.length === 0
                  ? <p className="text-sm text-gray-500 py-6 text-center italic">No invalid rows.</p>
                  : <div className="space-y-2">
                    {preview.invalid.map((r: any) => (
                      <div key={r.rowNum} className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                        <AlertTriangle className="h-3.5 w-3.5 text-red-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs font-semibold text-red-800">Row {r.rowNum}: {r.name}</p>
                          {r.errors.map((e: string, i: number) => (
                            <p key={i} className="text-xs text-red-600">{e}</p>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
              )}

              {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
            </div>
          )}

          {/* Step 3: Done */}
          {step === 'done' && result && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Import complete</p>
                  <p className="text-sm text-gray-500">
                    {result.created} created · {result.updated} updated
                    {result.failed?.length > 0 && ` · ${result.failed.length} failed`}
                  </p>
                </div>
              </div>

              {result.createdEmployees?.filter((e: any) => e.generatedPin).length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-xs font-semibold text-yellow-800 mb-2">Auto-generated PINs — save these now</p>
                  <div className="space-y-1.5">
                    {result.createdEmployees.filter((e: any) => e.generatedPin).map((e: any) => (
                      <div key={e.rowNum} className="flex items-center justify-between text-xs">
                        <span className="text-yellow-800 font-medium">{e.name}</span>
                        <span className="font-mono text-yellow-900 bg-yellow-100 border border-yellow-300 px-2 py-0.5 rounded">{e.pin}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {result.failed?.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-xs font-semibold text-red-800 mb-1.5">Failed rows</p>
                  {result.failed.map((f: any, i: number) => (
                    <p key={i} className="text-xs text-red-700">Row {f.rowNum} ({f.name}): {f.error}</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center px-5 py-4 border-t border-gray-200 flex-shrink-0">
          {step === 'upload' && (
            <>
              <button onClick={onClose} className="btn-secondary">Cancel</button>
              <button onClick={runDryRun} disabled={!file || loading} className="btn-primary">
                {loading ? 'Processing…' : 'Preview Import'}
              </button>
            </>
          )}
          {step === 'preview' && (
            <>
              <button onClick={() => setStep('upload')} className="btn-secondary">← Back</button>
              <button onClick={applyImport} disabled={loading || totalToImport === 0} className="btn-primary">
                {loading ? 'Importing…' : totalToImport === 0
                  ? 'Nothing to import'
                  : `Import ${totalToImport} employee${totalToImport !== 1 ? 's' : ''}`}
              </button>
            </>
          )}
          {step === 'done' && (
            <div className="w-full flex justify-end">
              <button onClick={onClose} className="btn-primary">Close</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Employee profile: jobs done, measured hours, efficiency, pay ───────────────
function EmployeeProfileModal({ emp, onClose, onEdit }: { emp: any; onClose: () => void; onEdit: () => void }) {
  const { data, isLoading } = useQuery(['employee-stats', emp.id], () => api.get(`/employees/${emp.id}/stats`).then(r => r.data));
  const t = data?.totals || {};
  const pay = data?.pay || {};
  const fmtD = (iso?: string | null) => iso ? new Date(String(iso).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';
  const money = (n: any) => `$${Number(n || 0).toFixed(2)}`;
  const Tile = ({ label, value, sub, tone }: { label: string; value: React.ReactNode; sub?: string; tone?: string }) => (
    <div className={`rounded-xl px-3 py-2.5 ${tone || 'bg-gray-50'}`}>
      <p className="text-[10px] font-bold uppercase tracking-wide text-gray-500">{label}</p>
      <p className="text-xl font-black text-gray-900 leading-tight">{value}</p>
      {sub && <p className="text-[11px] text-gray-500">{sub}</p>}
    </div>
  );
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-full bg-brand-100 flex items-center justify-center"><span className="text-base font-bold text-brand-700">{emp.name.charAt(0)}</span></div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">{emp.name}</h3>
              <p className="text-xs text-gray-500">
                {emp.hourly_rate > 0 ? `$${Number(emp.hourly_rate).toFixed(2)}/hr` : 'no rate'}{(emp.hat_words || []).length ? ` · ${emp.hat_words.join(', ')}` : ''}
                {emp.venmo_handle ? ` · @${String(emp.venmo_handle).replace(/^@/, '')}` : ' · no Venmo handle'}
                {emp.phone ? ` · ${emp.phone}` : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onEdit} className="text-xs text-brand-600 hover:text-brand-800 font-medium flex items-center gap-1"><Edit2 className="h-3.5 w-3.5" /> Edit</button>
            <button onClick={onClose}><X className="h-5 w-5 text-gray-500 hover:text-gray-600" /></button>
          </div>
        </div>
        <div className="px-5 py-4 overflow-y-auto space-y-4">
          {isLoading ? <p className="text-sm text-gray-500 py-6 text-center">Loading…</p> : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Tile label="Jobs done" value={t.completed_jobs ?? 0} sub={t.first_date ? `${fmtD(t.first_date)} → ${fmtD(t.last_date)}` : 'nothing yet'} />
                <Tile label="Days worked" value={t.days_worked ?? 0} sub={`${t.completed_by_them ?? 0} finished by them`} />
                <Tile label="Measured hours" value={t.measured_hours ?? 0} sub={`${t.measured_jobs ?? 0} jobs with a clock`} />
                <Tile label="Efficiency" value={t.efficiency_pct != null ? `${t.efficiency_pct}%` : '—'}
                  sub={t.efficiency_pct != null ? (t.efficiency_pct >= 100 ? 'faster than estimate' : 'slower than estimate') : 'needs measured jobs'}
                  tone={t.efficiency_pct == null ? 'bg-gray-50' : t.efficiency_pct >= 100 ? 'bg-green-50' : t.efficiency_pct >= 80 ? 'bg-amber-50' : 'bg-red-50'} />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Tile label="Owed" value={money(pay.owed?.total)} sub={`${pay.owed?.count || 0} entries`} tone="bg-amber-50" />
                <Tile label="Sent" value={money(pay.sent?.total)} sub={`${pay.sent?.count || 0} entries`} tone="bg-blue-50" />
                <Tile label="Confirmed" value={money(pay.confirmed?.total)} sub={`${pay.confirmed?.count || 0} entries`} tone="bg-green-50" />
              </div>
              {(data?.routes || []).length > 0 && (
                <p className="text-xs text-gray-500">Routes: {(data.routes as any[]).map((r: any) => `${r.name} × ${r.days}`).join(' · ')}{t.skipped_jobs ? ` · ${t.skipped_jobs} skipped stops` : ''}{t.upcoming_jobs ? ` · ${t.upcoming_jobs} upcoming` : ''}</p>
              )}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Recent jobs</p>
                {(data?.recent || []).length === 0 ? <p className="text-sm text-gray-500">No finished jobs yet.</p> : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead><tr className="text-left text-gray-500 border-b border-gray-100">
                        <th className="py-1.5 pr-2 font-semibold">Date</th><th className="py-1.5 pr-2 font-semibold">Customer</th>
                        <th className="py-1.5 pr-2 font-semibold text-right">Est</th><th className="py-1.5 pr-2 font-semibold text-right">Actual</th>
                        <th className="py-1.5 pr-2 font-semibold text-right">Pay</th><th className="py-1.5 font-semibold">Status</th>
                      </tr></thead>
                      <tbody>
                        {(data.recent as any[]).map((j: any) => {
                          const eff = j.est_minutes && j.actual_minutes ? Math.round((j.est_minutes / j.actual_minutes) * 100) : null;
                          return (
                            <tr key={j.id} className="border-b border-gray-50">
                              <td className="py-1.5 pr-2 whitespace-nowrap text-gray-700">{fmtD(j.date)}</td>
                              <td className="py-1.5 pr-2 text-gray-900">{j.customer_name || j.title || '—'}<span className="text-gray-500"> {j.route_name ? `· ${j.route_name}` : j.service_type ? `· ${j.service_type}` : ''}</span></td>
                              <td className="py-1.5 pr-2 text-right text-gray-600">{j.est_minutes ? `${j.est_minutes}m` : '—'}</td>
                              <td className={`py-1.5 pr-2 text-right font-medium ${eff == null ? 'text-gray-500' : eff >= 100 ? 'text-green-700' : eff >= 80 ? 'text-amber-700' : 'text-red-700'}`}>{j.actual_minutes ? `${j.actual_minutes}m` : '—'}{eff != null ? ` (${eff}%)` : ''}</td>
                              <td className="py-1.5 pr-2 text-right text-gray-700">{j.pay_amount != null ? money(j.pay_amount) : '—'}</td>
                              <td className="py-1.5"><span className={`px-1.5 py-0.5 rounded-full ${j.status === 'complete' ? 'bg-green-100 text-green-700' : j.status === 'skipped' ? 'bg-gray-100 text-gray-500' : 'bg-yellow-100 text-yellow-700'}`}>{j.is_partial ? 'partial' : j.status.replace('_', ' ')}</span>{j.pay_status ? <span className="ml-1 text-gray-500">{j.pay_status}</span> : null}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <ReviewPanel empId={emp.id} name={emp.name} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * How somebody is doing, on the record.
 *
 * Four things a season of route work shows fairly, and three questions worth
 * more than any of them. The last — what you both agreed for next time — is the
 * reason for writing the other six down: without it a review is an opinion, and
 * with it there is something to come back to.
 *
 * Pay is deliberately not here. That is decided in the pay ledger, and keeping
 * them apart is what stops a review turning into a negotiation.
 */
function ReviewPanel({ empId, name }: { empId: number; name: string }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<any>({ quality: 0, timekeeping: 0, equipment_care: 0, attitude: 0, going_well: '', to_work_on: '', agreed_next: '' });
  const qc = useQueryClient();

  const { data } = useQuery(['employee-reviews', empId], () =>
    api.get('/employee-reviews', { params: { crew_member_id: empId } }).then(r => r.data));
  const reviews: any[] = data?.reviews || [];
  const fields: any[] = data?.fields || [];

  async function save() {
    setSaving(true);
    try {
      const { data: r } = await api.post('/employee-reviews', { crew_member_id: empId, ...form });
      toast.success(r.message);
      setOpen(false);
      setForm({ quality: 0, timekeeping: 0, equipment_care: 0, attitude: 0, going_well: '', to_work_on: '', agreed_next: '' });
      qc.invalidateQueries(['employee-reviews', empId]);
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Could not save that');
    } finally { setSaving(false); }
  }

  const nothingSaid = !Object.values(form).some(v => (typeof v === 'number' ? v > 0 : String(v).trim()));

  return (
    <div className="mt-5 pt-4 border-t border-gray-200">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-bold text-gray-500 uppercase tracking-widest">Reviews</h4>
        <button onClick={() => setOpen(v => !v)} className="text-xs text-brand-600 font-medium">
          {open ? 'Cancel' : 'Write one'}
        </button>
      </div>

      {open && (
        <div className="space-y-3 rounded-xl border border-gray-200 p-3.5 mb-3">
          {fields.map(f => (
            <div key={f.key} className="flex items-center justify-between gap-3">
              <span className="text-sm text-gray-700">{f.label}</span>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map(n => (
                  <button key={n} onClick={() => setForm((p: any) => ({ ...p, [f.key]: p[f.key] === n ? 0 : n }))}
                    className={`h-7 w-7 rounded-lg text-xs font-semibold border ${
                      form[f.key] >= n ? 'bg-brand-600 text-black border-brand-600' : 'bg-white text-gray-500 border-gray-200'}`}>
                    {n}
                  </button>
                ))}
              </div>
            </div>
          ))}
          {([['going_well', "What's going well"], ['to_work_on', 'What to work on'], ['agreed_next', 'What you agreed for next time']] as const).map(([k, label]) => (
            <label key={k} className="block">
              <span className="text-xs font-medium text-gray-600">{label}</span>
              <textarea value={form[k]} onChange={e => setForm((p: any) => ({ ...p, [k]: e.target.value }))} rows={2}
                className="mt-1 w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm" />
            </label>
          ))}
          <button onClick={save} disabled={saving || nothingSaid}
            className="press w-full py-2.5 rounded-xl bg-brand-600 text-black text-sm font-semibold disabled:opacity-40">
            {saving ? 'Saving…' : `Save review for ${name.split(' ')[0]}`}
          </button>
        </div>
      )}

      {reviews.length === 0 ? (
        <p className="text-xs text-gray-500">No reviews yet.</p>
      ) : (
        <ul className="space-y-2">
          {reviews.map(r => (
            <li key={r.id} className="rounded-xl border border-gray-200 p-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">{String(r.created_at).slice(0, 10)}{r.reviewer ? ` · ${r.reviewer}` : ''}</span>
                {r.average != null && <span className="text-sm font-bold text-gray-900">{r.average} / 5</span>}
              </div>
              {r.going_well && <p className="text-sm text-gray-700 mt-1.5"><b className="text-gray-500 text-xs uppercase tracking-wide">Going well</b><br />{r.going_well}</p>}
              {r.to_work_on && <p className="text-sm text-gray-700 mt-1.5"><b className="text-gray-500 text-xs uppercase tracking-wide">To work on</b><br />{r.to_work_on}</p>}
              {r.agreed_next && <p className="text-sm text-gray-700 mt-1.5"><b className="text-gray-500 text-xs uppercase tracking-wide">Agreed</b><br />{r.agreed_next}</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SimpleModal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <h3 className="text-base font-black uppercase tracking-tight text-gray-900">{title}</h3>
          <button onClick={onClose}><X className="h-5 w-5 text-gray-500 hover:text-gray-600" /></button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

// ── Tidy up ───────────────────────────────────────────────────────────────
// Owner, Sep 18 2026: "no duplicates except AI 1 and 2, and ask about people
// who haven't worked in 3 weeks." The server finds them; this asks; nothing
// changes until he presses the button. A folded double keeps its history.
function TidyModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const readOnly = useReadOnly();   // view-only login: grey out what can only ever be refused
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery('employees-tidy', () => api.get('/employees/tidy').then(r => r.data), { staleTime: 0, cacheTime: 0 });
  const [keep, setKeep] = useState<Record<number, number>>({});          // double group index → id kept
  const [fold, setFold] = useState<Record<number, boolean>>({});         // double group index → fold it in?
  const [letGo, setLetGo] = useState<Record<number, boolean>>({});       // idle person id → take off the roster?
  const [dropLogin, setDropLogin] = useState<Record<number, boolean>>({}); // login group index → remove the double?
  const [result, setResult] = useState<string | null>(null);
  const apply = useMutation(() => {
    const body = {
      merges: (data?.doubles || []).map((g: any, i: number) => (fold[i] ?? true) ? { keep_id: keep[i] ?? g.keep_id, drop_ids: g.people.map((p: any) => p.id).filter((id: number) => id !== (keep[i] ?? g.keep_id)) } : null).filter(Boolean),
      deactivate: (data?.idle || []).filter((p: any) => letGo[p.id]).map((p: any) => p.id),
      remove_logins: (data?.logins || []).map((g: any, i: number) => (dropLogin[i] ?? true) ? g.drop_ids.map((d: number) => ({ drop_id: d, keep_id: g.keep_id })) : []).flat(),
    };
    return api.post('/employees/tidy', body).then(r => r.data);
  }, {
    onSuccess: (d: any) => { setResult(d.message); toast.success(d.message, { autoClose: 12000 }); onDone(); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not tidy the list'); },
  });
  const when = (d: string | null) => d ? new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'never';
  const nothing = data && !data.doubles.length && !data.idle.length && !data.logins.length;
  const planned = data ? (data.doubles as any[]).filter((_: any, i: number) => fold[i] ?? true).length + Object.values(letGo).filter(Boolean).length + (data.logins as any[]).filter((_: any, i: number) => dropLogin[i] ?? true).length : 0;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-black uppercase tracking-tight text-gray-900">Tidy up the people list</h2>
            <p className="text-xs text-gray-500">Nothing changes until you press the button at the bottom.{data?.ai_kept?.length ? ` ${data.ai_kept.join(' and ')} are left alone.` : ''}</p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-500 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        <div className="overflow-y-auto px-5 py-4 space-y-6 text-sm">
          {isLoading && <p className="text-gray-500">Looking the list over…</p>}
          {error && <p className="text-red-600">Could not look the list over.</p>}
          {nothing && !result && <p className="text-gray-600 flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-500" /> No doubles, no doubled logins, nobody idle three weeks. Nothing to tidy.</p>}
          {result && <p className="text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">{result}</p>}

          {!result && <HistoryTable onMoved={() => { qc.invalidateQueries('employees'); qc.invalidateQueries('employees-tidy'); qc.invalidateQueries('employees-history'); }} />}

          {data?.doubles?.length > 0 && !result && (
            <section>
              <h3 className="font-semibold text-gray-900">Same person twice <span className="text-gray-500 font-normal">· pick the one to keep; the other folds into it and keeps its history</span></h3>
              <div className="mt-2 space-y-3">
                {data.doubles.map((g: any, i: number) => (
                  <div key={i} className={`rounded-xl border p-3 ${(fold[i] ?? true) ? 'border-gray-200' : 'border-gray-100 opacity-60'}`}>
                    <div className="space-y-1.5">
                      {g.people.map((p: any) => (
                        <label key={p.id} className="flex items-start gap-2 cursor-pointer">
                          <input type="radio" name={`keep-${i}`} className="mt-1" checked={(keep[i] ?? g.keep_id) === p.id} onChange={() => setKeep(k => ({ ...k, [i]: p.id }))} disabled={!(fold[i] ?? true)} />
                          <span>
                            <span className="font-medium text-gray-900">{p.name}</span>
                            <span className="text-gray-500"> · {p.phone || 'no phone'} · {p.jobs} job{Number(p.jobs) === 1 ? '' : 's'} · last worked {when(p.last_worked)}{Number(p.is_active) ? '' : ' · inactive'}{p.login_id ? ' · has a login' : ''}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                    <label className="mt-2 flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                      <input type="checkbox" checked={fold[i] ?? true} onChange={e => setFold(f => ({ ...f, [i]: e.target.checked }))} /> Fold these into one
                    </label>
                  </div>
                ))}
              </div>
            </section>
          )}

          {data?.idle?.length > 0 && !result && (
            <section>
              <h3 className="font-semibold text-gray-900">No work in {data.idle_days} days <span className="text-gray-500 font-normal">· still on the roster — keep them, or take them off?</span></h3>
              <div className="mt-2 divide-y divide-gray-100 rounded-xl border border-gray-200">
                {data.idle.map((p: any) => (
                  <div key={p.id} className="flex items-center justify-between gap-3 px-3 py-2">
                    <div>
                      <p className="font-medium text-gray-900">{p.name}</p>
                      <p className="text-xs text-gray-500">{p.days_idle == null ? 'never put on anything' : `last worked ${when(p.last_worked)} · ${p.days_idle} days ago`}{p.login_id ? ' · has a login' : ''}</p>
                    </div>
                    <div className="flex rounded-lg border border-gray-300 overflow-hidden text-xs">
                      <button type="button" onClick={() => setLetGo(l => ({ ...l, [p.id]: false }))} className={`press px-3 py-1.5 ${!letGo[p.id] ? 'bg-brand-600 text-black' : 'bg-white text-gray-700'}`}>Keep</button>
                      <button type="button" onClick={() => setLetGo(l => ({ ...l, [p.id]: true }))} className={`px-3 py-1.5 ${letGo[p.id] ? 'bg-red-600 text-white' : 'bg-white text-gray-700'}`}>Take off</button>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-1 text-xs text-gray-500">Taking someone off deactivates them; their pay history and jobs stay. Bring them back under "Where the history lives" below — Add Employee would make a duplicate.</p>
            </section>
          )}

          {data?.logins?.length > 0 && !result && (
            <section>
              <h3 className="font-semibold text-gray-900">Logins that look doubled <span className="text-gray-500 font-normal">· the admin or manager one stays; with the hat switch one login is enough</span></h3>
              <div className="mt-2 space-y-2">
                {data.logins.map((g: any, i: number) => (
                  <label key={i} className="flex items-start gap-2 rounded-xl border border-gray-200 p-3 cursor-pointer">
                    <input type="checkbox" className="mt-1" checked={dropLogin[i] ?? true} onChange={e => setDropLogin(d => ({ ...d, [i]: e.target.checked }))} />
                    <span>
                      {g.logins.map((u: any, j: number) => (
                        <span key={u.id}>{j > 0 ? ' · ' : ''}<span className={j === 0 ? 'font-medium text-gray-900' : 'text-gray-700'}>{u.name}</span> <span className="text-gray-500">({u.role}{j === 0 ? ', kept' : ', removed'})</span></span>
                      ))}
                    </span>
                  </label>
                ))}
              </div>
            </section>
          )}
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-between gap-3">
          <p className="text-xs text-gray-500">{result ? 'Done.' : nothing ? '' : `${planned} change${planned === 1 ? '' : 's'} picked`}</p>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700">{result ? 'Close' : 'Cancel'}</button>
            {!result && !nothing && data && (
              <button onClick={() => apply.mutate()} disabled={readOnly || (apply.isLoading || planned === 0)} className="btn-primary text-sm disabled:opacity-50">
                {apply.isLoading ? 'Tidying…' : 'Make these changes'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Sign-in links ─────────────────────────────────────────────────────────
// Owner, Oct 1 2026: "they dont know pins yet so reset the pins so they can
// make there own pins" — then "not all of them". Nobody is ticked until he
// ticks them. Resetting someone clears their PIN (the old one stops working)
// and texts them a one-time link to make their own. The server never lists
// or resets the owner (any Admin hat), the AI profiles, or inactive people.
type PinPerson = {
  id: number; name: string; phone: string | null; has_phone: boolean; has_pin: boolean;
  needs_pin_link: boolean; status: string; status_text: string;
};
type PinResult = { id: number; name: string | null; phone?: string; outcome: string; why?: string; url?: string; body?: string };

const PIN_STATUS_TONE: Record<string, string> = {
  own: 'text-emerald-700',
  link_sent: 'text-amber-700',
  link_expired: 'text-red-700',
  needs_link: 'text-red-700',
  office: 'text-gray-600',
  no_pin: 'text-red-700',
};
const OUTCOME_WORDS: Record<string, string> = {
  texted: 'texted the link',
  not_set_up: "reset — texting isn't set up, send this link yourself",
  failed: 'reset, but the text failed — send this link yourself',
  no_phone: 'not reset — no phone',
  refused: 'not reset',
  error: 'not reset — something went wrong',
};

function PinLinksModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const readOnly = useReadOnly();
  const { data, isLoading, error, refetch } = useQuery('employees-pin-links',
    () => api.get('/employees/pin-links').then(r => r.data as { people: PinPerson[]; left_out: { id: number; name: string; why: string }[]; sms_configured: boolean }),
    { staleTime: 0, cacheTime: 0 });
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [results, setResults] = useState<{ message: string; results: PinResult[] } | null>(null);
  const people = data?.people || [];
  const tickable = people.filter(p => p.has_phone);
  // Only people who cannot sign in now (no PIN, or reset and their link ran
  // out). Anyone on an office PIN is ticked by name only — the owner, Oct 1:
  // "not all of them daniel sean ryan atkisnon and james and michale are good".
  const noPinYet = tickable.filter(p => ['no_pin', 'needs_link', 'link_expired'].includes(p.status));
  const chosen = people.filter(p => picked.has(p.id));
  const toggle = (id: number) => setPicked(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const send = useMutation(() => api.post('/employees/pin-links', { crew_member_ids: chosen.map(p => p.id) }).then(r => r.data), {
    onSuccess: (d: any) => {
      setResults(d); setPicked(new Set());
      toast.success(d.message || 'Done', { autoClose: 8000 });
      refetch(); onDone();
    },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not send the links'); },
  });
  const go = () => {
    if (!chosen.length) return;
    const names = chosen.map(p => p.name).join(', ');
    if (window.confirm(`Reset the PIN for ${chosen.length === 1 ? '' : `these ${chosen.length} people: `}${names}?\n\nTheir old PIN stops working right away. Each gets a text with a link to make their own (works once, 14 days).`)) send.mutate();
  };
  const copy = async (text: string) => {
    try { await navigator.clipboard.writeText(text); toast.success('Copied'); }
    catch { window.prompt('Copy this', text); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-xl sm:rounded-2xl rounded-t-2xl max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-black uppercase tracking-tight text-gray-900">Sign-in links</h2>
            <p className="text-xs text-gray-500">Tick only the people who don't know their PIN. Each one's PIN is reset and they get a text with a link to make their own.</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="p-2 -mr-2 text-gray-500 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        <div className="overflow-y-auto px-5 py-4 space-y-4 text-sm">
          {isLoading && <p className="text-gray-500">Loading the list…</p>}
          {error && <p className="text-red-600">Could not load the list.</p>}
          {data && !data.sms_configured && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">Texting isn't set up here — you'll get each link to send yourself.</p>
          )}
          {results && (
            <div className="rounded-xl border border-gray-200 divide-y divide-gray-100">
              <p className="px-3 py-2 text-xs font-semibold text-gray-800 bg-gray-50 rounded-t-xl">{results.message}</p>
              {results.results.map(r => (
                <div key={r.id} className="px-3 py-2 text-xs">
                  <p><span className="font-semibold text-gray-900">{r.name || `#${r.id}`}</span>
                    <span className={r.outcome === 'texted' ? 'text-emerald-700' : 'text-gray-600'}> · {OUTCOME_WORDS[r.outcome] || r.outcome}{r.outcome === 'refused' && r.why ? ` (${r.why})` : ''}</span></p>
                  {r.url && (
                    <div className="mt-1 flex items-start gap-2">
                      <p className="flex-1 font-mono break-all bg-gray-50 border border-gray-200 rounded-lg px-2 py-1 select-all">{r.url}</p>
                      <button type="button" onClick={() => copy(r.body || r.url || '')} className="press shrink-0 px-2 py-1 rounded-lg border border-gray-300 font-semibold">Copy text</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {data && (
            <>
              <div className="flex flex-wrap gap-2">
                <button type="button" disabled={!noPinYet.length} onClick={() => setPicked(new Set(noPinYet.map(p => p.id)))}
                  className="press text-xs px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 disabled:opacity-50">
                  Tick everyone with no PIN yet ({noPinYet.length})
                </button>
                <button type="button" disabled={!picked.size} onClick={() => setPicked(new Set())}
                  className="press text-xs px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 disabled:opacity-50">Clear</button>
              </div>
              {people.length === 0 ? <p className="text-gray-500">Nobody on the crew app to send a link to.</p> : (
                <ul className="rounded-xl border border-gray-200 divide-y divide-gray-100">
                  {people.map(p => (
                    <li key={p.id}>
                      <label className={`flex items-start gap-3 px-3 py-2.5 ${p.has_phone ? 'cursor-pointer' : 'opacity-60'}`}>
                        <input type="checkbox" className="mt-1" disabled={!p.has_phone} checked={picked.has(p.id)} onChange={() => toggle(p.id)} />
                        <span className="flex-1 min-w-0">
                          <span className="block font-medium text-gray-900">{p.name}</span>
                          <span className="block text-xs text-gray-500">{p.has_phone ? p.phone : 'no phone — add one to send a link'}</span>
                          <span className={`block text-xs mt-0.5 ${PIN_STATUS_TONE[p.status] || 'text-gray-600'}`}>{p.status_text}</span>
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
              {data.left_out.length > 0 && (
                <p className="text-xs text-gray-500">Never listed: {data.left_out.map(l => `${l.name} (${l.why})`).join(', ')}.</p>
              )}
            </>
          )}
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-between gap-3">
          <p className="text-xs text-gray-500">{chosen.length ? `${chosen.length} ticked` : 'Nobody ticked'}</p>
          <button type="button" onClick={go} disabled={readOnly || !chosen.length || send.isLoading} className="btn-primary text-sm disabled:opacity-50">
            {send.isLoading ? 'Sending…' : `Reset PIN and text the link to ${chosen.length} ${chosen.length === 1 ? 'person' : 'people'}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Which apps a person can open ──────────────────────────────────────────
function HatPicker({ value, onChange }: { value: string[]; onChange: (hats: string[]) => void }) {
  return (
    <div>
      <label className="label-sm">Apps they can open</label>
      <div className="flex flex-wrap gap-1.5 mt-1">
        {[...HATS, ...(value.includes('inspector') ? [OLD_INSPECTOR] : [])].map(h => {
          const on = value.includes(h.key);
          return (
            <button key={h.key} type="button" title={h.hint} onClick={() => onChange(on ? value.filter(k => k !== h.key) : [...value, h.key])}
              className={`press text-xs px-2.5 py-1.5 rounded-full border ${on ? 'bg-brand-600 text-black border-brand-600' : 'bg-white text-gray-700 border-gray-300'}`}>{h.label}</button>
          );
        })}
      </div>
      <p className="text-xs text-gray-500 mt-1">{value.length ? 'They switch between these from the profile menu.' : 'Pick at least one.'} A Manager also inspects the week's lawns.</p>
      {value.includes('inspector') && <p className="text-xs text-amber-700 mt-1">{value.some(k => k === 'manager' || k === 'admin') ? 'The old Inspector hat comes off when you save — the bigger hat already inspects.' : 'On the old Inspector hat. Tick Manager to give them the office side too; they keep inspecting either way.'}</p>}
    </div>
  );
}


// ── Where the history lives ───────────────────────────────────────────────
// Every person row, active or folded away, with what points at it: jobs,
// route days, pay entries, clock events. When a person's numbers sit on the
// wrong row, "move everything to" folds that row into the right one.
function HistoryTable({ onMoved }: { onMoved: () => void }) {
  const readOnly = useReadOnly();   // view-only login: grey out what can only ever be refused
  const { data } = useQuery('employees-history', () => api.get('/employees/history').then(r => r.data.people as any[]), { staleTime: 0, cacheTime: 0 });
  const [target, setTarget] = useState<Record<number, string>>({});
  const { loginWithToken } = useAuth();
  const move = useMutation(async ({ from, to }: { from: number; to: number }) => api.post(`/employees/${from}/fold`, { into_id: to }).then(r => r.data), {
    onSuccess: (d: any) => { if (d.token && d.user) loginWithToken(d.token, d.user); toast.success(d.message || 'Moved', { autoClose: 12000 }); onMoved(); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not move that'); },
  });
  // The undo for a mistaken remove: an inactive card is invisible on the list
  // and its crew PIN is locked out, so this is the one place to bring it back.
  const bringBack = useMutation(async (id: number) => api.put(`/employees/${id}`, { is_active: true }).then(r => r.data), {
    onSuccess: () => { toast.success('Back on the list — their PIN works again'); onMoved(); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not bring them back'); },
  });
  // An inactive, un-folded person always shows even with no history yet —
  // it's the only place their card can be brought back from.
  const people = (data || []).filter((p: any) => p.history > 0 || Number(p.is_active) === 1 || !p.merged_into);
  if (!people.length) return null;
  const active = people.filter((p: any) => Number(p.is_active) === 1 && !p.merged_into);
  return (
    <section>
      <h3 className="font-semibold text-gray-900">Where the history lives <span className="text-gray-500 font-normal">· jobs, route days, pay and clock rows per person</span></h3>
      <div className="mt-2 rounded-xl border border-gray-200 divide-y divide-gray-100 text-xs">
        {people.map((p: any) => (
          <div key={p.id} className={`px-3 py-2 ${p.merged_into || !Number(p.is_active) ? 'text-gray-500' : 'text-gray-800'}`}>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="font-medium">{p.name}</span>
              {(p.hat_words || []).map((w: string) => <span key={w} className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{w}</span>)}
              {p.merged_into ? <span>· folded into {p.merged_into_name}</span> : !Number(p.is_active) ? (
                <>
                  <span>· inactive</span>
                  <button type="button" disabled={readOnly || bringBack.isLoading}
                    onClick={() => bringBack.mutate(p.id)}
                    className="px-2 py-0.5 rounded-lg border border-emerald-300 bg-emerald-50 text-emerald-700 font-semibold disabled:opacity-50">
                    Bring back
                  </button>
                </>
              ) : null}
              <span className="ml-auto">{p.job_crew_rows + p.jobs_assigned} jobs · {p.route_days} route days{p.route_days_ahead ? ` (${p.route_days_ahead} ahead)` : ''} · {p.pay_entries} pay rows ${Number(p.pay_total || 0).toFixed(0)} · ${Number(p.hourly_rate || 0).toFixed(0)}/hr</span>
            </div>
            {Number(p.is_active) === 1 && !p.merged_into && p.history > 0 && active.length > 1 && (
              <div className="mt-1 flex items-center gap-2">
                <select value={target[p.id] || ''} onChange={e => setTarget(t => ({ ...t, [p.id]: e.target.value }))} className="input text-xs py-1 flex-1">
                  <option value="">Move everything from {p.name} to…</option>
                  {active.filter((o: any) => o.id !== p.id).map((o: any) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
                <button type="button" disabled={readOnly || (!target[p.id] || move.isLoading)}
                  onClick={() => { const to = active.find((o: any) => String(o.id) === target[p.id]); if (window.confirm(`Move everything from ${p.name} to ${to?.name}? ${p.name} goes inactive; jobs, route days, pay, clocks and the login all go to ${to?.name}.`)) move.mutate({ from: p.id, to: Number(target[p.id]) }); }}
                  className="px-2.5 py-1 rounded-lg border border-gray-300 bg-white text-gray-800 disabled:opacity-50">Move</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

/** The link a new crew member opens to make their PIN — copy it, text it, or share it. */
function JoinLinkModal({ invite, onClose }: { invite: { url: string; name?: string; phone?: string; days?: number }; onClose: () => void }) {
  const first = String(invite.name || '').split(' ')[0] || 'there';
  const text = `Hi ${first}, this is Keep Austin Mowed. Tap to make your 6-digit PIN for the crew app: ${invite.url}`;
  const digits = String(invite.phone || '').replace(/\D/g, '');
  const copy = async () => {
    try { await navigator.clipboard.writeText(invite.url); toast.success('Link copied'); }
    catch { window.prompt('Copy this link', invite.url); }
  };
  const share = async () => {
    try { await (navigator as any).share({ title: 'Make your PIN', text }); } catch { /* cancelled */ }
  };
  return (
    <SimpleModal title={`Link for ${invite.name || 'them'}`} onClose={onClose}>
      <div className="space-y-3">
        <p className="text-sm text-gray-700">Send this to {first}. They pick their own 6-digit PIN, check their phone, email and Venmo, and they're signed in. It works once and lasts {invite.days || 14} days.</p>
        <p className="text-xs font-mono break-all bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-2 select-all">{invite.url}</p>
        <div className="flex flex-wrap gap-2">
          <Link to={textsPath(digits, text)}
            className="press px-3 py-2 rounded-xl bg-brand-600 text-black text-sm font-semibold">Text it</Link>
          <button type="button" onClick={copy} className="press px-3 py-2 rounded-xl border border-gray-300 text-sm font-semibold">Copy link</button>
          {typeof (navigator as any).share === 'function' && (
            <button type="button" onClick={share} className="press px-3 py-2 rounded-xl border border-gray-300 text-sm font-semibold">Share…</button>
          )}
        </div>
      </div>
    </SimpleModal>
  );
}
